import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import Loader from "../../components/widgets/Loader";
import { useSelector } from "react-redux";
import { formattedProfile } from "../user/selectors";
import { useAppDispatch, useAutoFocus } from "../../app/hooks";
import ReactTooltip from "react-tooltip";
import { CheckCircleIcon, TrashIcon, XCircleIcon } from "@heroicons/react/solid";
import BaseModal from "../../components/widgets/modal/BaseModal";
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, RowDD, Sort, Toolbar } from "@syncfusion/ej2-react-grids";
import { show } from "../notification/notificationSlice";
import PinInput from "react-pin-input";
import { DashAlert } from "../../components/widgets/alerts/AlertElements";
import QRCode from "react-qr-code";
import { createSecondFactor, FactorDataType, FactorType, factorTypes, FactorValidationType, FactorViewType, getSecondFactors, resendSecondFactorCode, saveSecondFactors, validateSecondFactor } from "../../adapters";
import { TableLoader } from "../../components/widgets/SkeletonScreens";

const ManageAuthentication = () => {
	let userProfile = useSelector(formattedProfile);
	const dispatch = useAppDispatch();
	const inputFocus = useAutoFocus();
	const gridControl = useRef<any>("");
	const secondFactorLimit = process.env.REACT_APP_AUTH_FACTOR_LIMIT ? +process.env.REACT_APP_AUTH_FACTOR_LIMIT : 4; // auth factor limit (configurable via env variables)
	const factorViewRef = useRef<FactorViewType>();
	let resendTimeout: ReturnType<typeof setTimeout>;

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [showAddModal, setShowAddModal] = useState<boolean>(false);
	const [showValidationModal, setShowValidationModal] = useState<boolean>(false);
	const [validationProcessing, setValidationProcessing] = useState<boolean>(false);
	const [updateProcessing, setUpdateProcessing] = useState<boolean>(false);
	const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);
	const [factorType, setFactorType] = useState<FactorType>(factorTypes[0]);
	const [validationFactor, setValidationFactor] = useState<FactorValidationType>({
		type: "",
		enlistmentId: "",
		qrCodeUrl: "",
		canResendCodeInSec: 0,
	});
	const [factorView, setFactorView] = useState<FactorViewType>({
		access: {},
		enabledFactors: {},
		memberId: "",
		secondFactors: [],
	});

	useEffect(() => {
		if (userProfile) {
			setIsLoading(true);
			fetchData();
		}
		return () => {};
	}, [userProfile]);

	useEffect(() => {
		factorViewRef.current = factorView;
	}, [factorView]);

	const fetchData = async () => {
		if (userProfile?.loginId) {
			await getSecondFactors(userProfile.loginId)
				.then((response) => {
					setFactorView({
						access: response.data?.access ?? {},
						enabledFactors: response.data?.enabledFactors ?? {},
						memberId: userProfile.loginId,
						secondFactors: response.data?.secondFactors ?? [],
					});
				})
				.catch((error) => {
					showFactorError(error.message ?? "There was an error fetching second factors.");
				});

			setIsLoading(false);
		}
	};

	const updateFactorList = (updatedFactors: FactorDataType[]) => {
		// check if there is at least one enabled factor being saved - if not, show error and do not allow update
		const hasFactorEnabled = updatedFactors.find((uf) => uf.isEnabled === true);
		if (!hasFactorEnabled) {
			showFactorError("You must have at least one factor enabled at all times.")
			return;
		}

		// @note: timeout workaround for syncfusion drag and drop order
		setTimeout(async () => {
			setUpdateProcessing(true);
			await saveSecondFactors(userProfile.loginId, updatedFactors)
				.then((response) => {
					setFactorView(response.data);
				})
				.catch((error) => {
					showFactorError(error ?? "There was an error updating authentication factors. Please try again.");
				});
			setUpdateProcessing(false);
		}, 0);
	};

	const showFactorError = (error?: any) => {
		dispatch(
			show({
				show: true,
				title: "Error",
				message: error,
				status: "error",
				position: "popover",
				autoHide: 7000,
				confirm: false,
				notificationId: null,
			})
		);
	};

	// Formik/validation
	const getFactorInputValidation = () => {
		const phoneRegExp = /^[\+]?([0-9][\s]?|[0-9]?)([(][0-9]{3}[)][\s]?|[0-9]{3}[-\s\.]?)[0-9]{3}[-\s\.]?[0-9]{3,4}$/;
		switch (factorType.key) {
			case "sms":
				return Yup.string().matches(phoneRegExp, "Invalid Phone Number").required("A phone number is required");
			case "email":
				return Yup.string().email("Invalid email").required("An email address is required");
			case "totp":
				return Yup.string().required("Please enter a name for your authenticator factor");
		}
		return Yup.string().required("Please enter a value");
	};

	const formik = useFormik({
		initialValues: {
			factorValue: "",
		},
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			factorValue: getFactorInputValidation(),
		}),
		onSubmit: async (values, { resetForm }) => {
			setValidationProcessing(true);
			const createFactorData = {
				memberId: userProfile.memberId,
				type: factorType.key,
				emailPhoneOrName: values.factorValue,
				adminSkipValidation: false,
			};

			await createSecondFactor(createFactorData)
				.then((response) => {
					if (response.data.success) {
						const canResendInSeconds = response.data?.canResendCodeInSec ?? 300;

						setShowAddModal(false);
						setShowValidationModal(true);
						setIsResendDisabled(true);

						setValidationFactor(response.data);
						resetForm();

						// Disable the re-send button based on response time to re-enable
						resendTimeout = setTimeout(() => setIsResendDisabled(false), canResendInSeconds * 1000);
					} else {
						showFactorError(response.data.failureReason ?? "There was an error adding the second factor. Please try again.");
					}
				})
				.catch((error) => {
					showFactorError(error ?? "There was an error adding the second factor. Please try again.");
				});

			setValidationProcessing(false);
		},
	});

	const handleValidation = async (validationCode: string) => {
		if (validationFactor) {
			setValidationProcessing(true);
			await validateSecondFactor(validationFactor?.enlistmentId, validationCode)
				.then((response) => {
					if (response.data.success) {
						dispatch(
							show({
								show: true,
								title: "Success!",
								message: "Your second factor has been validated",
								status: "success",
								position: "popover",
								autoHide: 7000,
								confirm: false,
								notificationId: null,
							})
						);
						setShowValidationModal(false);
						fetchData();
					} else {
						showFactorError(response.data.failureReason ?? "There was an error validating the second factor. Please try again.");
					}
				})
				.catch((error: any) => {
					showFactorError(error.message ?? "There was an error validating the second factor. Please try again.");
					setShowValidationModal(false);
				});

			setValidationProcessing(false);
		}
	};

	// Factor Actions
	const addFactor = (factorType: FactorType) => {
		formik.resetForm();
		setShowAddModal(true);
		setFactorType(factorType);

		if (resendTimeout) {
			clearTimeout(resendTimeout);
		}
	};

	const handleToolbarClick = (args: any) => {
		if (args.item.id === "RefreshView") {
			fetchData();
		}
	};

	const handleRowDrop = (args: any) => {
		const updatedFactors = gridControl.current?.currentViewData;
		updateFactorList(updatedFactors);
	};

	const handleToggleEnabled = (factorId: number | string) => {
		let updatedFactors = factorViewRef.current?.secondFactors ?? [];
		const foundFactor = updatedFactors.find((factor) => factor.secondFactorId === factorId);
		if (foundFactor) {
			foundFactor.isEnabled = !foundFactor.isEnabled;
		}
		updateFactorList(updatedFactors);
	};

	const handleDelete = (factorId: number | string) => {
		if (window.confirm("Are you sure you wish to delete this factor?")) {
			let updatedFactors = factorViewRef.current?.secondFactors ?? [];
			updatedFactors = updatedFactors.filter((factor) => factor.secondFactorId !== factorId);

			updateFactorList(updatedFactors);
		}
	};

	const resendValidationCode = () => {
		if (validationFactor) {
			resendSecondFactorCode(validationFactor.enlistmentId);
		}
	};

	return isLoading ? (
		<Loader />
	) : (
		<div className="relative flex justify-center items-center w-full">
			<div className="w-full lg:w-3/4 p-5">
				<div className="px-8 text-center pb-4">
					<h3 className="pt-4 mb-2 text-2xl">Manage Authentications</h3>
					<p>
						User ID: <strong>{userProfile.loginId}</strong>
					</p>
				</div>
				<div className="p-4 text-sm border-t border-divider">
					{updateProcessing ? (
						<TableLoader />
					) : (
						<>
							<div className="flex justify-center items-center w-full">
								{factorView.secondFactors.length >= secondFactorLimit ? (
									<DashAlert
										notification={{
											show: true,
											title: "Authentication Type Limit Reached",
											message: `You have reached your allowed authentications limit (${secondFactorLimit} allowed). To add more, please delete an item below.`,
											status: "info",
											position: "dash",
											autoHide: false,
											confirm: false,
											notificationId: null,
											systemNotificationStatus: {
												color: "blue",
											},
										}}
										canClose={false}
									/>
								) : (
									<>
										{factorTypes.map((factorType, i) => {
											const enabledFactor = factorView.enabledFactors[factorType.key];
											if (enabledFactor) {
												return (
													<button key={i} type="button" className="w-full rounded-md shadow-sm px-4 py-2 bg-header text-sm font-normal text-white hover:bg-opacity-80 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-header focus:ring-opacity-50" onClick={() => addFactor(factorType)}>
														Add {factorType.display}
													</button>
												);
											}
											return false;
										})}
										<button
											type="button"
											className="w-full rounded-md shadow-sm px-4 py-2 bg-header text-sm font-normal text-white hover:bg-opacity-80 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-header focus:ring-opacity-50"
											onClick={() => {
												window.location.href = `${process.env.REACT_APP_AUTH_URL}/account/passkeys/enlist?returnUrl=${window.location}`;
											}}
										>
											Add Passkey
										</button>
									</>
								)}
							</div>
							<div className="syncfusion-grid w-full mt-4 auth-factors-table">
								<GridComponent
									ref={gridControl}
									allowRowDragAndDrop={true}
									rowDrop={handleRowDrop}
									dataSource={factorView.secondFactors}
									resizeSettings={{ mode: "Auto" }}
									dataBound={() => {
										gridControl.current.autoFitColumns([]);
									}}
									toolbar={[
										{
											text: "Refresh",
											tooltipText: "Refresh List",
											prefixIcon: "e-refresh",
											id: "RefreshView",
										},
									]}
									toolbarClick={handleToolbarClick}
								>
									<ColumnsDirective>
										<ColumnDirective field="type" headerText="Auth Type" isPrimaryKey={true} clipMode="EllipsisWithTooltip" />
										<ColumnDirective field="emailPhoneOrName" headerText="Entry" clipMode="EllipsisWithTooltip" />
										<ColumnDirective field="isEnabled" headerText="Enabled" clipMode="EllipsisWithTooltip" />
										<ColumnDirective
											field="actions"
											headerText="Actions"
											template={(props: any) => {
												return (
													<span className="relative z-0 inline-flex shadow-sm rounded-sm gap-2">
														{!props.isFromMemberData && (
															<button title="Delete" type="button" className="relative inline-flex items-center px-2 py-2 rounded-l-sm border border-default bg-primary" onClick={() => handleDelete(props.secondFactorId)}>
																<span className="sr-only">Delete</span>
																<TrashIcon className="w-4 h-4" aria-hidden="true" />
															</button>
														)}
														{props.isFromMemberData && (
															<button title="Toggle Enabled" type="button" className="relative inline-flex items-center px-2 py-2 rounded-l-sm border border-default bg-primary" onClick={() => handleToggleEnabled(props.secondFactorId)}>
																<span className="sr-only">Disable</span>
																{props.isEnabled === true ? <CheckCircleIcon className="w-4 h-4 text-green-500" aria-hidden="true" /> : <XCircleIcon className="w-4 h-4 text-red-500" aria-hidden="true" />}
															</button>
														)}
													</span>
												);
											}}
											textAlign="Right"
											allowSorting={false}
											clipMode="EllipsisWithTooltip"
										/>
									</ColumnsDirective>
									<Inject services={[Page, Sort, Toolbar, RowDD]} />
								</GridComponent>
							</div>
						</>
					)}
				</div>
			</div>
			<BaseModal open={showAddModal} setOpen={setShowAddModal}>
				<div className="p-4">
					{validationProcessing ? (
						<Loader />
					) : (
						<>
							<div className="px-8 text-center">
								<h3 className="pt-4 mb-2 text-2xl">Add {factorType.display}</h3>
							</div>
							<form id="authFactorForm" onSubmit={formik.handleSubmit} className="px-8 py-2">
								<div className="mb-4 relative">
									<input
										id="factorValue"
										type="text"
										className="basic-form-field"
										placeholder={factorType.key === "totp" ? "Enter a name for your authenticator app" : `Enter ${factorType.display}`}
										value={formik.values.factorValue}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										ref={inputFocus}
										onFocus={() => {
											ReactTooltip.hide();
										}}
									/>
									{formik.touched.factorValue && formik.errors.factorValue && <div className="text-xs text-red-600 text-right mt-1">{formik.errors.factorValue}</div>}
								</div>
								<div className="flex justify-center items-center w-full gap-2">
									<button className="w-full crmls-submit-btn" type="submit">
										Validate {factorType.display}
									</button>
									<button className="w-full rounded-md shadow-sm px-4 py-2 bg-red-500 text-base font-medium text-white hover:bg-opacity-80 sm:mt-0 sm:ml-3 sm:w-auto sm:text-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50" onClick={() => setShowAddModal(false)}>
										Cancel
									</button>
								</div>
							</form>
						</>
					)}
				</div>
			</BaseModal>
			<BaseModal open={showValidationModal} setOpen={setShowValidationModal}>
				<div className="p-4">
					{validationProcessing ? (
						<Loader />
					) : !validationFactor ? (
						<DashAlert
							notification={{
								show: true,
								title: "Error",
								message: `No data found for validation factor. Please close this window and try again.`,
								status: "error",
								position: "dash",
								autoHide: false,
								confirm: false,
								notificationId: null,
								systemNotificationStatus: {
									color: "red",
								},
							}}
							canClose={false}
						/>
					) : (
						<>
							{validationFactor?.qrCodeUrl && (
								<div className="p-4">
									<div className="flex justify-center">
										<QRCode value={validationFactor.qrCodeUrl} size={256} />
									</div>
								</div>
							)}
							<div className="px-8 text-center">
								<h3 className="pt-4 mb-2 text-2xl">Validate {factorType.display}</h3>
								<p>{validationFactor.type === "totp" ? `Please scan the QR code above and validate via your authentication app.` : `Please enter the code that was sent to your ${factorType.display}`}</p>
							</div>
							<div className="flex justify-center m-2 pb-2">
								<PinInput length={6} secret={false} type="numeric" onComplete={handleValidation} focus />
							</div>
							<div className="flex justify-center items-center w-full gap-2">
								<button className={`w-48 crmls-submit-btn ${isResendDisabled ? "bg-opacity-20 cursor-not-allowed" : ""}`} disabled={isResendDisabled} onClick={resendValidationCode}>
									Resend Code
								</button>
							</div>
						</>
					)}
				</div>
			</BaseModal>
		</div>
	);
};

export default ManageAuthentication;
